import { Link } from 'react-router';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';

/**
 * `<GlobalNav>` — fixed top nav, glass-base variant.
 *
 * Anatomy: h-16 fixed inset-x-0 top-0 z-50, full-width, no rounded corners (it bleeds
 * into the viewport edge). Contains:
 * 1. A "Skip to main content" link (visually hidden until focus, UI-SPEC § 9.1)
 * 2. The "House Hunter" wordmark linking to `/` (basename `/app/`)
 * 3. Section links: Properties · Map · Jobs · Admin
 * 4. A "+ Submit" CTA on the right
 *
 * All links use react-router `<Link>` against the basename `/app/`. So
 * `to="/"` resolves to `/app/`, `to="/jobs"` to `/app/jobs`, etc. The legacy
 * EJS routes (`/properties`, `/jobs`, `/map`, `/admin`, `/submit`) live at
 * the unprefixed paths and remain reachable until Phase 09 (MIGR-01).
 *
 * Phase 05 ships only `/app/` and `/app/_design/` — links to other `/app/*`
 * paths render the SPA fallback (404 in route table = the Placeholder route).
 * Plan 05-05 + Phase 06+ wire those routes for real.
 */
export function GlobalNav() {
  return (
    <GlassPanel
      as="nav"
      variant="base"
      aria-label="Global navigation"
      className="fixed inset-x-0 top-0 z-50 h-16 px-6 flex items-center gap-6 rounded-none border-x-0 border-t-0"
      style={{
        background: 'rgb(10 22 38 / 0.92)',
        backdropFilter: 'blur(30px) saturate(1.6)',
        WebkitBackdropFilter: 'blur(30px) saturate(1.6)',
      }}
    >
      <a
        href="#main"
        className={cn(
          'sr-only focus:not-sr-only',
          'focus:absolute focus:top-2 focus:left-2 focus:z-50',
          'focus:bg-ocean-950 focus:text-foam-50 focus:px-4 focus:py-2 focus:rounded-lg',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        )}
      >
        Skip to main content
      </a>
      <Link
        to="/"
        className="text-foam-50 font-semibold text-lg tracking-tight focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded"
      >
        House Hunter
      </Link>
      <ul className="flex items-center gap-5 text-sm font-semibold text-foam-50/80">
        <li>
          <Link
            to="/properties"
            className="hover:text-foam-50 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded"
            aria-label="Properties"
          >
            Properties
          </Link>
        </li>
        <li>
          <Link
            to="/map"
            className="hover:text-foam-50 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded"
            aria-label="Map"
          >
            Map
          </Link>
        </li>
        <li>
          <Link
            to="/jobs"
            className="hover:text-foam-50 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded"
            aria-label="Jobs"
          >
            Jobs
          </Link>
        </li>
        <li>
          <Link
            to="/admin"
            className="hover:text-foam-50 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded"
            aria-label="Admin"
          >
            Admin
          </Link>
        </li>
      </ul>
      <Link
        to="/submit"
        aria-label="Submit new property"
        className={cn(
          'ml-auto text-sm font-semibold text-foam-50 bg-foam-50/10 hover:bg-foam-50/15',
          'px-3 h-9 inline-flex items-center rounded-lg',
          'hover:ring-1 hover:ring-bronze-500/40',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        )}
      >
        + Submit
      </Link>
    </GlassPanel>
  );
}
